import type {RefinementCtx} from 'zod';
import z from 'zod';
import {TradeStatus} from '../trade/TradeStatus';

/** Fields used by extension **create** open/closed consistency checks (aligned with `tradeWriteSchema` exit fields). */
export interface TradeExtensionCreateOpenClosedRefinementInput {
  status: TradeStatus;
  exitPrice: number;
  closedAt: number;
}

/**
 * Extension-only rule: open trades use exit/close sentinels **`0`**; closed variants require positive exit and close time.
 * Not part of **`tradeWriteSchema`** — that object has no cross-field refinement (only per-field defaults and bounds).
 */
export function refineTradeExtensionCreateOpenClosed(
  val: TradeExtensionCreateOpenClosedRefinementInput,
  ctx: RefinementCtx,
): void {
  if (val.status === TradeStatus.OPEN) {
    if (val.exitPrice !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'exitPrice must be 0 when status is open',
        path: ['exitPrice'],
      });
    }
    if (val.closedAt !== 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'closedAt must be 0 when status is open',
        path: ['closedAt'],
      });
    }
    return;
  }
  if (val.exitPrice <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'exitPrice must be positive when status is not open',
      path: ['exitPrice'],
    });
  }
  if (val.closedAt <= 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'closedAt must be a positive epoch ms when status is not open',
      path: ['closedAt'],
    });
  }
}

/** Fields used by extension **update** exit/close patch refinements. */
export interface TradeExtensionUpdateExitRefinementInput {
  exitPrice?: number;
  closedAt?: number;
  status?: TradeStatus;
}

/**
 * Optional exit/close fields must appear together; blocks invalid **`0`** / non-**`0`** combinations on patch payloads.
 */
export function refineTradeExtensionUpdateExitPairing(
  val: TradeExtensionUpdateExitRefinementInput,
  ctx: RefinementCtx,
): void {
  if (val.exitPrice === undefined && val.closedAt !== undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'exitPrice must be provided if closedAt is set',
      path: ['exitPrice'],
    });
  }

  if (val.exitPrice !== undefined && val.closedAt === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'closedAt must be provided if exitPrice is set',
      path: ['closedAt'],
    });
  }

  if (val.exitPrice !== 0 && val.closedAt === 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'closedAt must be provided if exitPrice is set',
      path: ['closedAt'],
    });
  }

  if (val.exitPrice === 0 && val.closedAt !== 0) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'exitPrice must be provided if closedAt is set',
      path: ['exitPrice'],
    });
  }
}

/** When both exit and close are present on an update patch, **`status`** must be set and not **`open`**. */
export function refineTradeExtensionUpdateCloseStatus(
  val: TradeExtensionUpdateExitRefinementInput,
  ctx: RefinementCtx,
): void {
  if (val.exitPrice !== undefined && val.closedAt !== undefined && val.status === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'status must be set when closing trade',
      path: ['status'],
    });
  }

  if (val.exitPrice !== undefined && val.closedAt !== undefined && val.status === TradeStatus.OPEN) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'status must be set to closed/sl/tp when closing trade',
      path: ['status'],
    });
  }
}
